import React, { useState } from 'react';
import { Quest } from '../types';
import QuestDetailsModal from './QuestDetailsModal';
import QuestEditModal from './QuestEditModal';

interface QuestCardProps {
  quest: Quest;
  onComplete: (questId: string) => void;
  onDelete: (questId: string, silent?: boolean, deleteSeries?: boolean) => void;
  onUpdate?: (questId: string, updates: Partial<Quest>) => void;
  onToggleSubTask?: (questId: string, subTaskId: string) => void;
}

const QuestCard: React.FC<QuestCardProps> = ({ quest, onComplete, onDelete, onUpdate, onToggleSubTask }) => {
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  
  // Only show details modal for resources (subtasks are shown inline)
  const hasDetails = quest.resources && quest.resources.length > 0;
  
  // Quest Difficulty Ranks (separate from Hunter Rank achievements)
  // E-Rank = Easy, C-Rank = Normal, A-Rank = Hard, S-Rank = Boss
  // Gaps (no D/B rank) are intentional for difficulty scaling
  const difficultyConfig = {
    easy: { 
      color: 'border-rank-e', 
      textColor: 'text-rank-e',
      glow: 'shadow-[0_0_15px_rgba(148,163,184,0.3)]',
      label: 'E-RANK',
      iconPath: '/achievements/ranks/rank_e_hunter.png',
      stars: 1,
      starColor: 'text-gray-400'
    },
    normal: { 
      color: 'border-rank-c', 
      textColor: 'text-rank-c',
      glow: 'shadow-[0_0_15px_rgba(59,130,246,0.3)]',
      label: 'C-RANK',
      iconPath: '/achievements/ranks/rank_c_hunter.png',
      stars: 3,
      starColor: 'text-blue-400'
    },
    hard: { 
      color: 'border-rank-a', 
      textColor: 'text-rank-a',
      glow: 'shadow-[0_0_15px_rgba(245,158,11,0.3)]',
      label: 'A-RANK',
      iconPath: '/achievements/ranks/rank_a_hunter.png',
      stars: 5,
      starColor: 'text-amber-400'
    },
    boss: { 
      color: 'border-rank-s', 
      textColor: 'text-rank-s',
      glow: 'shadow-[0_0_20px_rgba(239,68,68,0.5)]',
      label: 'S-RANK',
      iconPath: '/achievements/ranks/rank_s_hunter.png',
      stars: 6,
      starColor: 'text-red-500'
    },
  };

  const typeIcons = {
    daily: '/icons/icon-quest-daily.png',
    main: '/icons/icon-quest-main.png',
    instant: '/icons/icon-quest-instant.png',
    boss: '/icons/icon-quest-boss.png',
  };

  const renderRankStars = (stars: number, color: string) => {
    return (
      <div className="flex gap-0.5">
        {[...Array(stars)].map((_, i) => (
          <span key={i} className={`${color} text-xs sm:text-sm drop-shadow-[0_0_3px_currentColor]`}>
            ★
          </span>
        ))}
      </div>
    );
  };

  const isCompleted = quest.status === 'completed';
  const config = difficultyConfig[quest.difficulty];

  const formatScheduledDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('default', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // Check if all subtasks are completed
  const allSubTasksCompleted = quest.subTasks && quest.subTasks.length > 0 
    ? quest.subTasks.every(st => st.completed)
    : true;

  return (
    <div className="relative group">
      {/* Quest Card */}
      <div
        onClick={() => hasDetails && setShowDetailsModal(true)}
        className={`relative bg-gradient-to-b from-slate-800/50 via-slate-900/50 to-slate-950/50 backdrop-blur-sm border rounded-3xl p-4 sm:p-6 transition-all ${
          isCompleted
            ? 'border-green-500/30 opacity-50'
            : `border-2 ${config.color} ${config.glow} hover:${config.glow} hover:scale-[1.02]`
        } ${hasDetails ? 'cursor-pointer' : ''}`}
        style={{
          boxShadow: '0 6px 18px rgba(0, 0, 0, 0.5), 0 2px 6px rgba(0, 0, 0, 0.3), inset 0 1px 3px rgba(255, 255, 255, 0.08), inset 0 -2px 6px rgba(0, 0, 0, 0.4)'
        }}
      >
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-start justify-between mb-3 sm:mb-4 gap-2">
          <div className="flex items-start gap-2 sm:gap-3 flex-1">
            <img 
              src={typeIcons[quest.type]} 
              alt={quest.type}
              className="w-6 h-6 sm:w-8 sm:h-8 object-contain flex-shrink-0"
              style={{ filter: 'drop-shadow(0 0 6px rgba(59, 130, 246, 0.8))' }}
            />
            <div className="flex-1 min-w-0">
              <div className="flex flex-col sm:flex-row sm:items-center gap-1.5 sm:gap-2">
                <h3 className={`font-bold font-rajdhani text-sm sm:text-base ${isCompleted ? 'line-through text-gray-500' : 'text-white'} truncate`}>
                  {quest.title}
                </h3>
                {hasDetails && (
                  <span className="text-xs bg-system-blue/20 border border-system-blue/50 text-system-blue px-2 py-0.5 rounded-full font-orbitron font-bold uppercase tracking-wider animate-pulse flex-shrink-0">
                    📋 Details
                  </span>
                )}
              </div>
              <p className="text-xs sm:text-sm text-gray-400 mt-1 font-rajdhani line-clamp-2">
                {quest.description}
              </p>
              {/* Scheduled Date */}
              {quest.scheduledFor && (
                <div className="flex items-center gap-1.5 mt-1">
                  <span className="text-xs">📅</span>
                  <span className="text-xs text-purple-400 font-rajdhani font-semibold">
                    {formatScheduledDate(quest.scheduledFor)}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Difficulty Badge with Stars */}
          <div className="flex flex-row sm:flex-col items-center sm:items-end gap-1.5 sm:gap-1.5">
            <div className={`px-2 sm:px-3 py-1.5 ${config.color} ${config.textColor} rounded-lg font-orbitron text-xs font-bold tracking-wider flex items-center gap-1.5 sm:gap-1.5`}>
              <img 
                src={config.iconPath} 
                alt={config.label}
                className="w-3 h-3 sm:w-4 sm:h-4 object-contain"
                style={{ filter: 'drop-shadow(0 0 4px currentColor)' }}
              />
              <span className="hidden sm:inline">{config.label}</span>
              <span className="sm:hidden">{config.label.split('-')[0]}</span>
            </div>
            <div className="hidden sm:block">
              {renderRankStars(config.stars, config.starColor)}
            </div>
            <div className="sm:hidden flex">
              {renderRankStars(Math.min(config.stars, 3), config.starColor)}
            </div>
          </div>
        </div>

        {/* Sub-tasks */}
        {quest.subTasks && quest.subTasks.length > 0 && (
          <div className="mb-3 sm:mb-4 bg-dark-800/50 border border-green-500/20 rounded-lg p-2 sm:p-3">
            <div className="flex items-center gap-1.5 mb-2">
              <span className="text-sm">✓</span>
              <span className="text-xs text-gray-400 font-rajdhani uppercase tracking-wide">
                Sub-Tasks ({quest.subTasks.filter(st => st.completed).length}/{quest.subTasks.length})
              </span>
            </div>
            <div className="space-y-1.5 sm:space-y-2">
              {quest.subTasks.map((subTask) => (
                <button
                  key={subTask.id}
                  onClick={() => !isCompleted && onToggleSubTask && onToggleSubTask(quest.id, subTask.id)}
                  disabled={isCompleted}
                  className={`w-full flex items-center gap-2 p-1.5 sm:p-2 rounded-lg transition-all ${
                    isCompleted 
                      ? 'cursor-default' 
                      : 'hover:bg-dark-700/50 cursor-pointer'
                  }`}
                >
                  <div className={`w-3 h-3 sm:w-4 sm:h-4 border-2 rounded flex items-center justify-center transition-all flex-shrink-0 ${
                    subTask.completed 
                      ? 'bg-green-500 border-green-500' 
                      : 'border-green-500/50 hover:border-green-500'
                  }`}>
                    {subTask.completed && (
                      <span className="text-white text-xs font-bold">✓</span>
                    )}
                  </div>
                  <span className={`text-xs sm:text-sm font-rajdhani transition-all line-clamp-2 ${
                    subTask.completed 
                      ? 'line-through text-gray-500' 
                      : 'text-gray-300'
                  }`}>
                    {subTask.text}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Rewards & Actions */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between mt-3 sm:mt-4 pt-3 sm:pt-4 border-t border-system-blue/20 gap-2 sm:gap-0">
          <div className="flex gap-3 sm:gap-4 text-xs sm:text-sm font-rajdhani flex-wrap">
            {/* XP Reward */}
            <div className="flex items-center gap-1.5">
              <img 
                src="/icons/icon-xp.png" 
                alt="XP"
                className="w-3 h-3 sm:w-4 sm:h-4 object-contain"
                style={{ filter: 'drop-shadow(0 0 4px rgba(59, 130, 246, 0.8))' }}
              />
              <span className="text-system-blue font-bold text-sm sm:text-base glow-text">
                +{quest.xpReward}
              </span>
              <span className="text-xs text-gray-400">XP</span>
            </div>

            {/* Currency Reward */}
            <div className="flex items-center gap-1.5">
              <img 
                src="/icons/icon-gold.png" 
                alt="Gold"
                className="w-4 h-4 sm:w-5 sm:h-5 object-contain"
                style={{ filter: 'drop-shadow(0 0 4px rgba(252, 211, 77, 0.8))' }}
              />
              <span className="font-bold text-sm sm:text-base font-orbitron" style={{ color: '#FCD34D' }}>
                +{quest.currencyReward}
              </span>
              <span className="text-xs text-gray-400">Gold</span>
            </div>

            {/* Stat Rewards */}
            {Object.keys(quest.statRewards).length > 0 && (
              <div className="flex items-center gap-1.5">
                <span className="text-xs text-gray-400">Stats:</span>
                <span className="text-system-purple font-bold text-sm sm:text-base">
                  +{Object.values(quest.statRewards).reduce((a, b) => a + b, 0)}
                </span>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex gap-1.5">
            {!isCompleted ? (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onComplete(quest.id);
                }}
                disabled={!allSubTasksCompleted}
                className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg flex items-center justify-center font-orbitron text-xs font-bold tracking-wide uppercase transition-all ${
                  allSubTasksCompleted
                    ? 'bg-gradient-to-r from-green-600 to-green-500 text-white border border-green-500 hover:shadow-lg hover:shadow-green-500/30 hover:scale-105'
                    : 'bg-gray-600 text-gray-400 border border-gray-500 opacity-30 cursor-not-allowed'
                }`}
                title={!allSubTasksCompleted ? 'Complete all sub-tasks first' : 'Complete quest'}
              >
                <span className="relative z-10">Complete</span>
              </button>
            ) : (
              <div className="px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg flex items-center justify-center font-orbitron text-xs font-bold tracking-wide uppercase text-green-400 opacity-80 bg-gradient-to-r from-green-600/20 to-green-500/20 border border-green-500/50">
                <span className="relative z-10">✓ Done</span>
              </div>
            )}
            
            {onUpdate && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowEditModal(true);
                }}
                className="w-6 h-6 sm:w-8 sm:h-8 rounded-lg flex items-center justify-center bg-gradient-to-br from-blue-900/30 to-blue-800/30 border border-blue-500/50 hover:border-blue-500 text-blue-400 hover:text-blue-300 transition-all hover:scale-105"
                title="Edit quest"
              >
                <img 
                  src="/icons/icon-edit.png" 
                  alt="Edit"
                  className="w-3 h-3 sm:w-4 sm:h-4 object-contain relative z-10"
                  style={{ filter: 'drop-shadow(0 0 4px rgba(59, 130, 246, 0.6))' }}
                />
              </button>
            )}
            
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (quest.type === 'daily') {
                  // For daily quests, show choice dialog
                  const choice = window.confirm(
                    'Delete this daily quest?\n\n• OK: Delete only this instance (quest continues tomorrow)\n• Cancel: Choose to delete entire series'
                  );
                  if (choice) {
                    // OK: Delete only this instance (quest resets tomorrow)
                    onDelete(quest.id);
                  } else {
                    // Cancel: Ask for confirmation to delete entire series
                    if (window.confirm('⚠️ Delete ENTIRE recurring daily quest series?\n\nThis will permanently stop this daily quest from appearing again.')) {
                      onDelete(quest.id, false, true); // deleteSeries = true
                    }
                  }
                } else {
                  // For regular quests, normal delete confirmation
                  if (window.confirm('Delete this quest?')) {
                    onDelete(quest.id);
                  }
                }
              }}
              className="w-6 h-6 sm:w-8 sm:h-8 rounded-lg flex items-center justify-center bg-gradient-to-br from-red-900/30 to-red-800/30 border border-red-500/50 hover:border-red-500 text-red-400 hover:text-red-300 transition-all hover:scale-105"
              title={quest.type === 'daily' ? 'Delete daily quest (choose instance or entire series)' : 'Delete quest'}
            >
              <img
                src="/icons/icon-delete.png"
                alt="Delete"
                className="w-3 h-3 sm:w-4 sm:h-4 object-contain relative z-10"
                style={{ filter: 'drop-shadow(0 0 4px rgba(239, 68, 68, 0.6))' }}
              />
            </button>
          </div>
        </div>

        {/* Warning message for incomplete subtasks */}
        {!isCompleted && !allSubTasksCompleted && (
          <div className="mt-2 sm:mt-3 text-xs text-yellow-400 font-rajdhani flex items-center gap-1.5">
            <span>⚠️</span>
            <span>Complete all sub-tasks to finish this quest</span>
          </div>
        )}

        {/* Completion overlay effect */}
        {isCompleted && (
          <div className="absolute inset-0 bg-green-500/5 rounded-xl pointer-events-none" />
        )}
      </div>
      
      {/* Quest Details Modal - Only show for quests with details */}
      {hasDetails && (
        <QuestDetailsModal
          quest={quest}
          isOpen={showDetailsModal}
          onClose={() => setShowDetailsModal(false)}
        />
      )}
      
      {/* Quest Edit Modal */}
      {onUpdate && (
        <QuestEditModal
          quest={quest}
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          onUpdate={(questId, updates) => {
            onUpdate(questId, updates);
            setShowEditModal(false);
          }}
          onDelete={(questId) => {
            onDelete(questId);
            setShowEditModal(false);
          }}
          onComplete={(questId) => {
            onComplete(questId);
            setShowEditModal(false);
          }}
        />
      )}
      
    </div>
  );
};

export default QuestCard;
